/**
 * auth.js
 *
 * Simple localStorage-based auth for Know Your Guy.
 * Stores a users registry and current session.
 */
import { supabase } from "./supabase";



export async function getCurrentUser() {
  const { data: { session } } = await supabase.auth.getSession();

  if (!session) return null;

  return {
    id: session.user.id,
    email: session.user.email,
  };
}

export async function signIn(email, password) {
  if (!email || !password) {
    return { ok: false, error: "Please enter your email and password." };
  }

  let timeoutId;
  const timeout = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error('timeout')), 15000);
  });

  try {
    const { data, error } = await Promise.race([
      supabase.auth.signInWithPassword({ email, password }),
      timeout,
    ]);

    clearTimeout(timeoutId);

    if (error) return { ok: false, error: error.message };

    return { ok: true, user: { id: data.user?.id, email: data.user?.email } };
  } catch (err) {
    clearTimeout(timeoutId);
    if (err.message === 'timeout') {
      return { ok: false, error: "Sign in timed out. Check your connection and try again." };
    }
    return { ok: false, error: "Something went wrong. Please try again." };
  }
}

const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Does the email's domain actually exist? Checks MX records via Google's
// DNS-over-HTTPS API (CORS-enabled), falling back to an A-record check
// (RFC 5321 allows mail delivery to A-only domains). FAILS OPEN: any
// network/API problem allows the signup — this must never lock out a real
// user just because the DNS check itself is down. Sign-in is never gated.
async function emailDomainExists(email) {
  const domain = email.split("@")[1]?.trim().toLowerCase();
  if (!domain || !domain.includes(".")) return false;
  try {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), 4000);
    const res = await fetch(
      `https://dns.google/resolve?name=${encodeURIComponent(domain)}&type=MX`,
      { signal: ctl.signal }
    );
    clearTimeout(timer);
    if (!res.ok) return true; // DNS API unavailable -> fail open
    const mx = await res.json();
    if (mx.Status === 3) return false; // NXDOMAIN: domain does not exist
    if (Array.isArray(mx.Answer) && mx.Answer.length > 0) return true;
    const resA = await fetch(
      `https://dns.google/resolve?name=${encodeURIComponent(domain)}&type=A`
    );
    if (!resA.ok) return true;
    const a = await resA.json();
    return a.Status !== 3 && Array.isArray(a.Answer) && a.Answer.length > 0;
  } catch {
    return true; // network error -> fail open
  }
}

export async function signUp(email, password, username) {
  if (!email || !password) {
    return { ok: false, error: "Please enter email and password." };
  }

  if (!EMAIL_FORMAT.test(email.trim())) {
    return { ok: false, error: "That doesn't look like a valid email address." };
  }

  if (!(await emailDomainExists(email.trim()))) {
    return { ok: false, error: "That email domain doesn't appear to exist. Double-check the address." };
  }

  const { data, error } = await supabase.auth.signUp({
    email,
    password,
  });

  if (error) {
    return { ok: false, error: error.message };
  }

  await supabase
    .from("profiles")
    .update({ username })
    .eq("id", data.user.id);

  const user = {
    id: data.user?.id,
    email: data.user?.email,
    username
  };

  return { ok: true, user };
}

export async function signOut() {
  await supabase.auth.signOut();
}


export async function updatePassword(newPassword) {
  if (!newPassword || newPassword.length < 6) {
    return { ok: false, error: "Password must be at least 6 characters." };
  }

  const { error } = await supabase.auth.updateUser({
    password: newPassword,
  });

  if (error) {
    return { ok: false, error: error.message };
  }

  return { ok: true };
}
export async function requestPasswordReset(email) {
  if (!email) {
    return { ok: false, error: "Enter your email first." };
  }

  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: 'https://www.knowyourguy.com/reset-password',
  });

  if (error) {
    return { ok: false, error: error.message };
  }

  return { ok: true };
}